import { CircleAlert, Info } from 'lucide-react'
import { Card } from '@/components/ui/card'
import { CurrencyValue, DateValue } from '@/components/common'
import type { OfferDecisionContext } from '@/features/recruitment'

/**
 * The offer, against the requisition it answers to.
 *
 * <p>An offer approved in isolation is how a pay structure comes apart, one reasonable-looking
 * exception at a time. The range note is the recruiter's own sentence, carried through rather
 * than recomputed — a second opinion computed twice is two opinions.
 */
export function OfferApprovalContext({ detail }: { detail: OfferDecisionContext }) {
  const { offer, requisition, candidate } = detail
  const outsideRange = offer.withinSalaryRange === false

  return (
    <div className="space-y-4">
      <Card className="p-4">
        <h3 className="text-sm font-semibold text-[var(--color-heading)]">
          {offer.reference} · {candidate.displayName}
        </h3>
        <p className="mt-2 text-sm text-[var(--color-muted)]">
          Basic <CurrencyValue value={offer.basicSalary} currency={offer.currency ?? undefined} />
          {offer.totalPackage != null && (
            <>
              {' '}· package{' '}
              <CurrencyValue value={offer.totalPackage} currency={offer.currency ?? undefined} />
            </>
          )}
          {offer.joiningDate && <> · joining <DateValue value={offer.joiningDate} /></>}
        </p>
        <p className="mt-1 text-sm text-[var(--color-muted)]">
          Against {requisition.reference} · {requisition.title}
        </p>
      </Card>

      <Card
        className={`flex items-start gap-2 p-4 ${
          outsideRange || offer.withinSalaryRange == null
            ? 'border border-[var(--color-warning)]' : ''
        }`}
      >
        {offer.withinSalaryRange === true
          ? <Info className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
          : <CircleAlert className="mt-0.5 size-4 shrink-0 text-[var(--color-warning)]"
              aria-hidden="true" />}
        <div className="text-sm">
          <p className="font-medium text-[var(--color-heading)]">
            {offer.withinSalaryRange === true
              ? 'Inside the requisition’s range'
              : outsideRange
                ? 'Outside the requisition’s range'
                : 'Nothing to check the salary against'}
          </p>
          <p className="mt-1 text-[var(--color-muted)]">
            {detail.rangeNote ?? offer.rangeNote
              ?? 'The requisition names no salary range, so nothing was compared.'}
          </p>
          {detail.validityNote && (
            <p className="mt-1 text-[var(--color-muted)]">{detail.validityNote}</p>
          )}
        </div>
      </Card>
    </div>
  )
}
